/**
 * API client immobilier (Phase 2 — David & Co / DCO).
 * Endpoints /v1/operations/immobilier/*
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type {
  LotImmo, Programme, ReservationVefa,
  StatutLotImmo, StatutProgramme, StatutReservationVefa, TypeBien,
} from '@/types';
import type { Paginated } from './operations';

// ── Programmes ─────────────────────────────────────────────────────────────

export interface ProgrammeListParams {
  statut?: StatutProgramme;
  ville?:  string;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateProgrammePayload {
  code:                 string;
  nom:                  string;
  typeProgramme?:       string;
  adresse?:             string;
  quartier?:            string;
  ville?:               string;
  surfaceTerrainM2?:    number;
  budgetTotalCents:     number;
  budgetDepenseCents?:  number;
  dateDebut?:           string;
  dateLivraisonPrevue?: string;
  statut?:              StatutProgramme;
  nbLotsTotal?:         number;
  notes?:               string;
}

export type UpdateProgrammePayload = Partial<CreateProgrammePayload>;

// ── Lots immo ──────────────────────────────────────────────────────────────

export interface LotImmoListParams {
  statut?:      StatutLotImmo;
  typeBien?:    TypeBien;
  programmeId?: string;
  search?:      string;
  page?:        number;
  limit?:       number;
}

export interface CreateLotImmoPayload {
  code:             string;
  programmeId?:     string;
  designation?:     string;
  typeBien:         TypeBien;
  surfaceUtileM2?:  number;
  surfaceTotaleM2?: number;
  nbPieces?:        number;
  etage?:           number;
  prixVenteCents:   number;
  statut?:          StatutLotImmo;
  notes?:           string;
}

export type UpdateLotImmoPayload = Partial<CreateLotImmoPayload>;

// ── Réservations VEFA ──────────────────────────────────────────────────────

export interface ReservationVefaListParams {
  statut?:   StatutReservationVefa;
  lotId?:    string;
  clientId?: string;
  search?:   string;
  page?:     number;
  limit?:    number;
}

export interface CreateReservationVefaPayload {
  lotId:               string;
  clientId?:           string;
  reference?:          string;
  prixVenteCents:      number;
  acompteCumuleCents?: number;
  statut?:             StatutReservationVefa;
  dateSignature?:      string;
  notes?:              string;
}

export interface UpdateReservationVefaPayload {
  clientId?:           string;
  prixVenteCents?:     number;
  acompteCumuleCents?: number;
  statut?:             StatutReservationVefa;
  dateSignature?:      string;
  notes?:              string;
}

export interface AjoutAcomptePayload {
  montantCents: number;
  note?:        string;
}

// ── API ────────────────────────────────────────────────────────────────────

export const immobilierApi = {
  // Programmes
  listProgrammes: async (params: ProgrammeListParams = {}): Promise<Paginated<Programme>> => {
    const res = await apiClient.get('/operations/immobilier/programmes', { params });
    return unwrapPaginated<Programme>(res);
  },
  getProgramme: async (id: string): Promise<Programme> => {
    const res = await apiClient.get(`/operations/immobilier/programmes/${id}`);
    return unwrap<Programme>(res);
  },
  createProgramme: async (payload: CreateProgrammePayload): Promise<Programme> => {
    const res = await apiClient.post('/operations/immobilier/programmes', payload);
    return unwrap<Programme>(res);
  },
  updateProgramme: async (id: string, payload: UpdateProgrammePayload): Promise<Programme> => {
    const res = await apiClient.patch(`/operations/immobilier/programmes/${id}`, payload);
    return unwrap<Programme>(res);
  },
  deleteProgramme: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/immobilier/programmes/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // Lots
  listLots: async (params: LotImmoListParams = {}): Promise<Paginated<LotImmo>> => {
    const res = await apiClient.get('/operations/immobilier/lots', { params });
    return unwrapPaginated<LotImmo>(res);
  },
  getLot: async (id: string): Promise<LotImmo> => {
    const res = await apiClient.get(`/operations/immobilier/lots/${id}`);
    return unwrap<LotImmo>(res);
  },
  createLot: async (payload: CreateLotImmoPayload): Promise<LotImmo> => {
    const res = await apiClient.post('/operations/immobilier/lots', payload);
    return unwrap<LotImmo>(res);
  },
  updateLot: async (id: string, payload: UpdateLotImmoPayload): Promise<LotImmo> => {
    const res = await apiClient.patch(`/operations/immobilier/lots/${id}`, payload);
    return unwrap<LotImmo>(res);
  },
  deleteLot: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/immobilier/lots/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // Réservations VEFA
  listReservations: async (params: ReservationVefaListParams = {}): Promise<Paginated<ReservationVefa>> => {
    const res = await apiClient.get('/operations/immobilier/reservations-vefa', { params });
    return unwrapPaginated<ReservationVefa>(res);
  },
  getReservation: async (id: string): Promise<ReservationVefa> => {
    const res = await apiClient.get(`/operations/immobilier/reservations-vefa/${id}`);
    return unwrap<ReservationVefa>(res);
  },
  createReservation: async (payload: CreateReservationVefaPayload): Promise<ReservationVefa> => {
    const res = await apiClient.post('/operations/immobilier/reservations-vefa', payload);
    return unwrap<ReservationVefa>(res);
  },
  updateReservation: async (id: string, payload: UpdateReservationVefaPayload): Promise<ReservationVefa> => {
    const res = await apiClient.patch(`/operations/immobilier/reservations-vefa/${id}`, payload);
    return unwrap<ReservationVefa>(res);
  },
  ajouterAcompte: async (id: string, payload: AjoutAcomptePayload): Promise<ReservationVefa> => {
    const res = await apiClient.post(`/operations/immobilier/reservations-vefa/${id}/acompte`, payload);
    return unwrap<ReservationVefa>(res);
  },
  deleteReservation: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/immobilier/reservations-vefa/${id}`);
    return unwrap<{ deleted: true }>(res);
  },
};
